import { BrandMark, Diamond } from "./icons";
import type { Instrument } from "../api";

/**
 * Per-assessment opening copy.
 *
 * Headlines live here rather than on the server because they are design work,
 * not configuration — they get written alongside the layout they sit in. The
 * *counts* come from the API instead (see `facts` below), so the one thing that
 * would embarrass us if it drifted — telling someone twenty-one questions and
 * then asking thirty-two — cannot.
 *
 * An instrument with no entry falls back to its registry label and blurb, so
 * adding one to the registry ships a coherent intro screen without touching
 * this file.
 */
interface IntroCopy {
  headline: string;
  lede: string;
  hook?: string;
}

/** Named separately so it can be the fallback without an undefined check. */
const TRA_COPY: IntroCopy = {
  headline: "How ready is your business for what’s next?",
  lede:
    "Questions about how your company actually runs — its numbers, its systems, and how much " +
    "of it depends on you. You’ll get a readiness score and a scored summary by area.",
  hook: "“Do you need our help? Take five minutes and find out.”",
};

const COPY: Record<string, IntroCopy> = {
  traapp: TRA_COPY,
  sellability: {
    headline: "How attractive is your business to a buyer?",
    lede:
      "Questions a buyer would ask about your business — its revenue quality, its equipment, its " +
      "market, and how much of its value walks out the door with you. You’ll get a scored report " +
      "by category.",
    hook: "“Every business sells eventually. The question is on whose terms.”",
  },
};

/** Roughly nine seconds a question, rounded to a whole minute, floor of two. */
function minutes(questions: number): number {
  return Math.max(2, Math.round((questions * 9) / 60));
}

export function Intro({
  onStart,
  busy,
  instrument,
  onBack,
}: {
  onStart: () => void;
  busy: boolean;
  instrument: Instrument | null;
  /** Present only when the visitor arrived through the picker. */
  onBack?: () => void;
}) {
  // Falls back to the TRA's own copy, not to a generic paraphrase of it.
  //
  // `instrument` is null whenever the instruments call has not answered — a slow
  // network, or a blip. The TRA is the default assessment, so its copy is the
  // right thing to show; the earlier fallback quietly swapped in weaker wording
  // and dropped the hook line entirely, which meant an API hiccup silently cost
  // the first screen its best sentence.
  const copy =
    (instrument && COPY[instrument.slug]) ||
    (instrument
      ? { headline: instrument.label, lede: instrument.blurb, hook: undefined }
      : TRA_COPY);

  // Falls back to the TRA's own shape when the description hasn't loaded.
  const questions = instrument?.total_questions ?? 32;
  const areas = instrument?.total_categories ?? 8;

  return (
    <>
      <BrandMark />
      <h1>{copy.headline}</h1>
      <p className="lede">{copy.lede}</p>
      {copy.hook && <p className="hook">{copy.hook}</p>}
      <ul className="facts">
        <li>
          <b>{questions}</b>
          <span>Questions</span>
        </li>
        <li>
          <b>~{minutes(questions)} min</b>
          <span>To complete</span>
        </li>
        <li>
          <b>{areas}</b>
          <span>Areas scored</span>
        </li>
      </ul>
      <button className="btn btn-full" onClick={onStart} disabled={busy}>
        {busy ? "Starting…" : "Start the assessment"}
      </button>
      {onBack && (
        <button className="btn-quiet" onClick={onBack} disabled={busy}>
          Choose a different assessment
        </button>
      )}
      <p className="gate-assure" style={{ border: 0, paddingTop: 14, marginTop: 14 }}>
        <span style={{ color: "var(--citron-ink)", flexShrink: 0, marginTop: 2 }}>
          <Diamond />
        </span>
        <span>
          No payment, no obligation. We ask for your email at the end so we can send the report.
        </span>
      </p>
    </>
  );
}
